/* eslint-disable i18next/no-literal-string */
import { colors } from "~/components/landing/colors";
import { Picture } from "~/components/ui/picture";
import { imageUrl } from "~/lib/image-url";

// Plain public/ string paths, not JS imports — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const sharedLogins = imageUrl("/images/connecting-family/shared-logins.png");
const vehicleOverview = imageUrl("/images/connecting-family/vehicle-overview.png");
const mechanic = imageUrl("/images/connecting-family/mechanic.png");
const registrations = imageUrl("/images/connecting-family/registrations.png");

/**
 * Ported from bouncieweb's `Bento/MiscGrid.js` (plus the `GridItemLightBlueGradient`
 * and `BentoGridH3` helpers from `Bento/BentoComponents.js`).
 *
 * A 4-item bento grid: "Logins for Everyone", "A Picture of Health", "Stay
 * Informed", and "Renewal Reminders". Each card is a light-blue gradient tile
 * with a centered title and an image absolutely positioned along the bottom.
 *
 * The route renders <MiscGrid /> with no props, so this port reproduces the
 * NON-fleet variant only (the original defaulted `isFleet = false`); the
 * fleet-specific copy/images are intentionally omitted.
 *
 * Original breakpoint map (theme/mediaQueries):
 *   xsm 431px -> min-[431px]:  sm 640px -> sm:  md 768px -> md:
 *   lg 1024px -> lg:  xlg 1280px -> xl:
 *
 * Grid layout (from the `Grid` styled.div):
 *   base:  2 cols x 2 rows of 70vw
 *   xsm:   rows -> 65vw
 *   sm:    rows -> 22rem
 *   md:    4 cols x 1 row of 16rem
 *   lg:    row -> 21rem
 *   xl:    row -> 22rem
 */

// GridItemLightBlueGradient background: linear-gradient(blueLight1, white)
const lightBlueGradient = `linear-gradient(${colors.blueLight1}, ${colors.white})`;

type CardProps = {
  title: React.ReactNode;
  children: React.ReactNode;
};

// GridItemCustom (extends GridItemLightBlueGradient): rounded-[20px], relative,
// overflow-hidden, responsive padding, centered text.
function Card({ title, children }: CardProps) {
  return (
    <div
      className="relative overflow-hidden rounded-[20px] p-6 text-center md:p-8 lg:p-9 xl:p-10"
      style={{ background: lightBlueGradient }}
    >
      {/* H3Custom (BentoGridH3 override): display-extra-bold, tightened leading */}
      <h3 className="font-display text-[22px] leading-[1.2] font-extrabold md:text-[17px] lg:text-[24px]">
        {title}
      </h3>
      {children}
    </div>
  );
}

export default function MiscGrid() {
  return (
    // Grid
    <div className="grid grid-cols-2 grid-rows-[repeat(2,70vw)] gap-6 min-[431px]:grid-rows-[repeat(2,65vw)] sm:grid-rows-[repeat(2,22rem)] md:grid-cols-4 md:grid-rows-[16rem] lg:grid-rows-[21rem] xl:grid-rows-[22rem]">
      <Card
        title={
          <>
            Logins&nbsp;for
            <br />
            Everyone
          </>
        }
      >
        {/* SharedLogins: w-[90%], bottom-[10%], centered */}
        <Picture
          src={sharedLogins}
          alt="Two phones with two different users"
          className="absolute bottom-[10%] left-1/2 h-fit w-[90%] max-w-none -translate-x-1/2"
        />
      </Card>

      <Card
        title={
          <>
            A&nbsp;Picture
            <br />
            of&nbsp;Health
          </>
        }
      >
        {/* TroubleCode: w-[60%], bottom-[5%], centered */}
        <Picture
          src={mechanic}
          alt="Examples of diagnostic codes above a mechanic working on car"
          className="absolute bottom-[5%] left-1/2 h-fit w-[60%] max-w-none -translate-x-1/2"
        />
      </Card>

      <Card
        title={
          <>
            Stay
            <br />
            Informed
          </>
        }
      >
        {/* VehicleOverview: w-[90%], bottom-[20%], centered */}
        <Picture
          src={vehicleOverview}
          alt="Examples of vehicle health metrics above a mechanic working on car"
          className="absolute bottom-[20%] left-1/2 h-fit w-[90%] max-w-none -translate-x-1/2"
        />
      </Card>

      <Card
        title={
          <>
            Renewal
            <br />
            Reminders
          </>
        }
      >
        {/* DigitalGlovebox: w-[80%], bottom-[18%], centered */}
        <Picture
          src={registrations}
          alt="Reminders of renewals such as registration and emissions"
          className="absolute bottom-[18%] left-1/2 h-fit w-[80%] max-w-none -translate-x-1/2"
        />
      </Card>
    </div>
  );
}
